import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../components/ConfirmDialog';
import { BulkEditModal, BannerSettingsModal } from '../components/budgets/BudgetModals';
import NewPaymentModal from '../components/budgets/NewPaymentModal';
import EditPaymentModal from '../components/budgets/EditPaymentModal';
import BulkPaymentModal from '../components/budgets/BulkPaymentModal';
import PaymentsTable from '../components/budgets/PaymentsTable';
import { DollarSign, Plus, Trash2, Check, X, Search, Edit2, ArrowUpDown, Filter, Calendar, FileSpreadsheet, Settings, ChevronDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Budgets = () => {
  const { getAuthHeaders, user, hasPerm } = useAuth();
  const confirm = useConfirm();
  const [records, setRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [dateFilterActive, setDateFilterActive] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState({
    year: new Date().getFullYear(),
    monthFrom: new Date().getMonth() + 1,
    monthTo: new Date().getMonth() + 1
  });
  const [newRecord, setNewRecord] = useState({ employee_email: '', video_name: '', value: '', year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const [employeeProjects, setEmployeeProjects] = useState([]);

  // Filters
  const [sortAlpha, setSortAlpha] = useState(''); // '' | 'asc' | 'desc'
  const [paymentStatusFilter, setPaymentStatusFilter] = useState(''); // '' | 'pago' | 'nao_pago'
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState(''); // '' | 'nf_enviada' | 'esperando_nf' | 'sem_nf' | 'nao_necessario'

  // Edit
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({ video_name: '', value: '', year: '', month: '', employee_email: '' });
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditForm, setBulkEditForm] = useState({ video_name: '', year: '', month: '' });
  const [editEmployeeSearch, setEditEmployeeSearch] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [bannerText, setBannerText] = useState('');
  const [showBannerSettings, setShowBannerSettings] = useState(false);
  const [bannerDraft, setBannerDraft] = useState('');

  // Edit power follows the dynamic permission; the page itself is visible to everyone.
  // The backend restricts non-permitted users to read-only of their own records.
  const isAdmin = hasPerm('access_payments_page');
  const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  useEffect(() => { fetchRecords(); if (isAdmin) fetchUsers(); }, [selectedPeriod, dateFilterActive]);

  useEffect(() => { fetchBannerText(); }, []);

  const fetchRecords = async () => {
    try {
      const params = new URLSearchParams({ year: selectedPeriod.year });
      if (dateFilterActive) {
        params.append('month_from', selectedPeriod.monthFrom);
        params.append('month_to', selectedPeriod.monthTo);
      } else {
        params.append('month_from', 1);
        params.append('month_to', 12);
      }
      const response = await axios.get(`${API}/payment-records?${params}`, { headers: getAuthHeaders() });
      setRecords(response.data);
      setSelected(new Set());
    } catch { toast.error('Erro ao carregar pagamentos'); }
    finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { headers: getAuthHeaders() });
      setUsers(response.data);
    } catch (err) { console.error('Failed to fetch users:', err); }
  };

  const fetchBannerText = async () => {
    try {
      const response = await axios.get(`${API}/settings/payment_banner`, { headers: getAuthHeaders() });
      setBannerText(response.data.value || '');
    } catch (err) { console.error('Failed to fetch banner:', err); }
  };

  const saveBannerText = async () => {
    try {
      await axios.put(`${API}/settings/payment_banner`, { value: bannerDraft }, { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } });
      setBannerText(bannerDraft);
      setShowBannerSettings(false);
      toast.success('Texto atualizado!');
    } catch { toast.error('Erro ao salvar texto'); }
  };

  const handleExport = async (format) => {
    setShowExportMenu(false);
    try {
      const params = new URLSearchParams({ year: selectedPeriod.year });
      if (dateFilterActive) {
        params.append('month_from', selectedPeriod.monthFrom);
        params.append('month_to', selectedPeriod.monthTo);
      } else {
        params.append('month_from', 1);
        params.append('month_to', 12);
      }
      const response = await axios.get(`${API}/payment-exports/${format}?${params}`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const ext = format === 'excel' ? 'xlsx' : format;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `pagamentos_${selectedPeriod.year}.${ext}`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 500);
      toast.success(`Exportado como ${format.toUpperCase()}!`);
    } catch { toast.error('Erro ao exportar'); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('year', newRecord.year);
      formData.append('month', newRecord.month);
      formData.append('employee_email', newRecord.employee_email);
      formData.append('video_name', newRecord.video_name);
      formData.append('value', parseFloat(newRecord.value));
      await axios.post(`${API}/payment-records`, formData, { headers: getAuthHeaders() });
      toast.success('Pagamento registrado!');
      setShowModal(false);
      setNewRecord({ employee_email: '', video_name: '', value: '', year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
      fetchRecords();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao registrar pagamento'); }
  };

  const handleDelete = async (recordId) => {
    const ok = await confirm('Tem certeza que deseja excluir este registro?');
    if (!ok) return;
    try {
      await axios.delete(`${API}/payment-records/${recordId}`, { headers: getAuthHeaders() });
      toast.success('Registro excluído!'); fetchRecords();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao excluir registro'); }
  };

  const handlePaymentStatusToggle = async (recordId, currentStatus) => {
    try {
      const formData = new FormData();
      formData.append('payment_status', currentStatus === 'pago' ? 'nao_pago' : 'pago');
      await axios.put(`${API}/payment-records/${recordId}`, formData, { headers: getAuthHeaders() });
      toast.success('Status atualizado!'); fetchRecords();
    } catch { toast.error('Erro ao atualizar status'); }
  };

  const handleToggleNoInvoice = async (recordId, currentNoInvoice) => {
    try {
      const formData = new FormData();
      formData.append('no_invoice', currentNoInvoice ? 'false' : 'true');
      await axios.put(`${API}/payment-records/${recordId}`, formData, { headers: getAuthHeaders() });
      toast.success(currentNoInvoice ? 'NF reativada' : 'Marcado como sem NF');
      fetchRecords();
    } catch { toast.error('Erro ao atualizar'); }
  };

  const handleSetNotRequired = async (recordId, revert = false) => {
    try {
      const formData = new FormData();
      formData.append('invoice_status', revert ? 'esperando_nf' : 'nao_necessario');
      await axios.put(`${API}/payment-records/${recordId}`, formData, { headers: getAuthHeaders() });
      toast.success(revert ? 'NF reativada' : 'Marcado como Não Necessário');
      fetchRecords();
    } catch { toast.error('Erro ao atualizar'); }
  };

  const handlePdfUpload = async (recordId, file) => {
    if (!file) return;
    setUploadingPdf(recordId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/payment-records/${recordId}/pdf`, formData, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
      toast.success('PDF anexado!'); fetchRecords();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao anexar PDF'); }
    finally { setUploadingPdf(null); }
  };

  const handlePdfDownload = async (recordId, filename) => {
    try {
      // Cookie auth is sent automatically via credentials: 'include'
      const response = await fetch(`${API}/payment-records/${recordId}/pdf`, { credentials: 'include' });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      if (blob.size === 0) { toast.error('Arquivo PDF vazio'); return; }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename || 'nota_fiscal.pdf'; a.style.display = 'none';
      document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 500);
      toast.success('Download iniciado!');
    } catch { toast.error('Erro ao baixar PDF'); }
  };

  // Edit individual
  const handleEditClick = (record) => {
    setEditingRecord(record);
    setEditForm({
      video_name: record.video_name || '',
      value: record.value || '',
      year: record.year || new Date().getFullYear(),
      month: record.month || new Date().getMonth() + 1,
      employee_email: ''
    });
    setEditEmployeeSearch(record.employee_name || '');
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      if (editForm.video_name) formData.append('video_name', editForm.video_name);
      if (editForm.value) formData.append('value', parseFloat(editForm.value));
      if (editForm.year) formData.append('year', editForm.year);
      if (editForm.month) formData.append('month', editForm.month);
      if (editForm.employee_email) formData.append('employee_email', editForm.employee_email);
      await axios.put(`${API}/payment-records/${editingRecord.id}`, formData, { headers: getAuthHeaders() });
      toast.success('Pagamento atualizado!');
      setShowEditModal(false); setEditingRecord(null);
      fetchRecords();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao atualizar'); }
  };

  // Bulk edit
  const handleBulkEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const body = { record_ids: [...selected] };
      if (bulkEditForm.video_name) body.video_name = bulkEditForm.video_name;
      if (bulkEditForm.year) body.year = parseInt(bulkEditForm.year);
      if (bulkEditForm.month) body.month = parseInt(bulkEditForm.month);
      await axios.post(`${API}/payment-records/bulk-edit`, body, { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } });
      toast.success(`${selected.size} registros atualizados!`);
      setShowBulkEditModal(false); setBulkEditForm({ video_name: '', year: '', month: '' });
      fetchRecords();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao atualizar'); }
  };

  // Bulk actions
  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visible = getFilteredAndSorted();
    if (selected.size === visible.length) setSelected(new Set());
    else setSelected(new Set(visible.map(r => r.id)));
  };

  const handleBulkDelete = async () => {
    const ok = await confirm(`Excluir ${selected.size} registro(s) selecionado(s)?`);
    if (!ok) return;
    try {
      await axios.post(`${API}/payment-records/bulk-delete`, [...selected], { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } });
      toast.success(`${selected.size} registros excluídos!`); fetchRecords();
    } catch { toast.error('Erro ao excluir registros'); }
  };

  const handleBulkPay = async () => {
    const ok = await confirm(`Marcar ${selected.size} registro(s) como PAGO?`);
    if (!ok) return;
    try {
      await axios.post(`${API}/payment-records/bulk-pay`, [...selected], { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } });
      toast.success(`${selected.size} registros marcados como pagos!`); fetchRecords();
    } catch { toast.error('Erro ao atualizar registros'); }
  };

  const getEffectiveInvoiceStatus = (record) => {
    if (record.invoice_status === 'nao_necessario') return 'nao_necessario';
    if (record.no_invoice) return 'sem_nf';
    if (record.invoice_status === 'nf_enviada' || record.pdf_filename) return 'nf_enviada';
    return 'esperando_nf';
  };

  // Apply filters & sort
  const normalizeSearch = (s) => s?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const getFilteredAndSorted = () => {
    let result = records.filter(r => {
      if (search) {
        const q = normalizeSearch(search);
        if (!(normalizeSearch(r.video_name).includes(q) || normalizeSearch(r.employee_name).includes(q))) return false;
      }
      if (paymentStatusFilter && r.payment_status !== paymentStatusFilter) return false;
      if (invoiceStatusFilter && getEffectiveInvoiceStatus(r) !== invoiceStatusFilter) return false;
      return true;
    });

    if (sortAlpha === 'asc') {
      result = [...result].sort((a, b) => (a.employee_name || '').localeCompare(b.employee_name || ''));
    } else if (sortAlpha === 'desc') {
      result = [...result].sort((a, b) => (b.employee_name || '').localeCompare(a.employee_name || ''));
    }

    return result;
  };

  const filteredRecords = getFilteredAndSorted();

  const totalAmount = records.reduce((sum, r) => sum + (r.adjusted_value || r.value), 0);
  const totalPaid = records.filter(r => r.payment_status === 'pago').reduce((sum, r) => sum + (r.adjusted_value || r.value), 0);
  const totalPending = totalAmount - totalPaid;

  const filteredEmployees = users.filter(u => {
    if (!employeeSearch) return true;
    const q = employeeSearch.toLowerCase();
    return (u.name?.toLowerCase().includes(q) || u.artist_name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
  });

  const editFilteredEmployees = users.filter(u => {
    if (!editEmployeeSearch) return true;
    const q = editEmployeeSearch.toLowerCase();
    return (u.name?.toLowerCase().includes(q) || u.artist_name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
  });

  const hasActiveFilters = sortAlpha || paymentStatusFilter || invoiceStatusFilter;

  // Build user info map for tooltips
  const userMap = {};
  users.forEach(u => { userMap[u.id] = u; });

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-16 w-16 border-t-2 border-teal-400"></div></div></Layout>;
  }

  return (
    <Layout>
      <div className="p-6 md:p-10 space-y-6" data-testid="budgets-page">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <h1 className="text-3xl md:text-4xl font-bold text-white">Pagamentos</h1>
          <div className="flex gap-3 items-center flex-wrap">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Buscar..."
                className="bg-slate-700 border border-slate-600 rounded-lg pl-9 pr-3 py-1.5 text-white text-sm focus:outline-none focus:border-teal-500 w-48"
                data-testid="payment-search" />
            </div>
            {isAdmin && (
              <div className="relative">
                <button
                  onClick={() => setShowNewMenu(v => !v)}
                  className="flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg transition-colors"
                  data-testid="new-payment-btn"
                >
                  <Plus className="h-5 w-5" /> Novo <ChevronDown className="h-4 w-4" />
                </button>
                {showNewMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-20"
                      onClick={() => setShowNewMenu(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 bg-slate-800 border border-slate-700 rounded-xl shadow-lg z-30 min-w-[220px] overflow-hidden">
                      <button
                        onClick={() => { setShowNewMenu(false); setShowModal(true); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-700 transition-colors flex items-center gap-2"
                        data-testid="new-payment-option-single"
                      >
                        <Plus className="h-4 w-4 text-teal-400" /> Pagamento único
                      </button>
                      <button
                        onClick={() => { setShowNewMenu(false); setShowBulkModal(true); }}
                        className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-700 transition-colors flex items-center gap-2 border-t border-slate-700"
                        data-testid="new-payment-option-bulk"
                      >
                        <Plus className="h-4 w-4 text-purple-400" /> Pagamento em lote
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
            {/* Export Dropdown */}
            {isAdmin && (
              <div className="relative">
                <button onClick={() => setShowExportMenu(!showExportMenu)}
                  className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-colors"
                  data-testid="export-btn"
                >
                  <FileSpreadsheet className="h-5 w-5" /> Exportar <ChevronDown className="h-4 w-4" />
                </button>
                {showExportMenu && (
                  <div className="absolute right-0 top-full mt-2 bg-slate-800 border border-slate-700 rounded-xl shadow-lg z-30 min-w-[160px] overflow-hidden">
                    <button onClick={() => handleExport('excel')}
                      className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-700 transition-colors flex items-center gap-2"
                      data-testid="export-excel">
                      <span className="w-2 h-2 rounded-full bg-green-500" /> Excel (.xlsx)
                    </button>
                    <button onClick={() => handleExport('csv')}
                      className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-700 transition-colors flex items-center gap-2"
                      data-testid="export-csv">
                      <span className="w-2 h-2 rounded-full bg-blue-500" /> CSV (.csv)
                    </button>
                    <button onClick={() => handleExport('pdf')}
                      className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-700 transition-colors flex items-center gap-2"
                      data-testid="export-pdf">
                      <span className="w-2 h-2 rounded-full bg-red-500" /> PDF (.pdf)
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Date Filter */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setDateFilterActive(!dateFilterActive)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${dateFilterActive ? 'bg-teal-500 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
            data-testid="date-filter-toggle"
          >
            <Calendar className="h-4 w-4" />
            {dateFilterActive ? 'Filtro de Data Ativo' : 'Filtrar por Data'}
          </button>
          {dateFilterActive && (
            <div className="flex gap-2 items-center bg-slate-800/50 border border-slate-700 rounded-lg p-2">
              <select value={selectedPeriod.year} onChange={e => setSelectedPeriod({...selectedPeriod, year: parseInt(e.target.value)})}
                className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-teal-500" data-testid="year-selector">
                {[2023, 2024, 2025, 2026].map(year => <option key={year} value={year}>{year}</option>)}
              </select>
              <span className="text-slate-400 text-sm">de</span>
              <select value={selectedPeriod.monthFrom} onChange={e => setSelectedPeriod({...selectedPeriod, monthFrom: parseInt(e.target.value)})}
                className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-teal-500" data-testid="month-from-selector">
                {months.map((month, idx) => <option key={idx} value={idx + 1}>{month}</option>)}
              </select>
              <span className="text-slate-400 text-sm">a</span>
              <select value={selectedPeriod.monthTo} onChange={e => setSelectedPeriod({...selectedPeriod, monthTo: parseInt(e.target.value)})}
                className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-teal-500" data-testid="month-to-selector">
                {months.map((month, idx) => <option key={idx} value={idx + 1}>{month}</option>)}
              </select>
            </div>
          )}
          {!dateFilterActive && (
            <span className="text-slate-500 text-sm">Exibindo todos os meses de {selectedPeriod.year}</span>
          )}
        </div>

        {/* Filter Buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Sort Alphabetical */}
          <button
            onClick={() => setSortAlpha(prev => prev === '' ? 'asc' : prev === 'asc' ? 'desc' : '')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
              sortAlpha ? 'bg-blue-500/15 text-blue-400 border-blue-500/30' : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600'
            }`}
            data-testid="sort-alpha-btn"
          >
            <ArrowUpDown className="h-3.5 w-3.5" />
            Nome {sortAlpha === 'asc' ? '(A-Z)' : sortAlpha === 'desc' ? '(Z-A)' : ''}
          </button>

          {/* Payment Status Filter */}
          <button
            onClick={() => setPaymentStatusFilter(prev => prev === '' ? 'pago' : prev === 'pago' ? 'nao_pago' : '')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
              paymentStatusFilter === 'pago' ? 'bg-green-500/15 text-green-400 border-green-500/30'
              : paymentStatusFilter === 'nao_pago' ? 'bg-red-500/15 text-red-400 border-red-500/30'
              : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600'
            }`}
            data-testid="filter-payment-status-btn"
          >
            <DollarSign className="h-3.5 w-3.5" />
            {paymentStatusFilter === 'pago' ? 'Pago' : paymentStatusFilter === 'nao_pago' ? 'Não Pago' : 'Status Pgto'}
          </button>

          {/* Invoice Status Filter */}
          <button
            onClick={() => {
              const cycle = ['', 'esperando_nf', 'nf_enviada', 'sem_nf', 'nao_necessario'];
              const idx = cycle.indexOf(invoiceStatusFilter);
              setInvoiceStatusFilter(cycle[(idx + 1) % cycle.length]);
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
              invoiceStatusFilter === 'esperando_nf' ? 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30'
              : invoiceStatusFilter === 'nf_enviada' ? 'bg-green-500/15 text-green-400 border-green-500/30'
              : invoiceStatusFilter === 'sem_nf' ? 'bg-red-500/15 text-red-400 border-red-500/30'
              : invoiceStatusFilter === 'nao_necessario' ? 'bg-slate-500/15 text-slate-400 border-slate-500/30'
              : 'bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600'
            }`}
            data-testid="filter-invoice-status-btn"
          >
            <Filter className="h-3.5 w-3.5" />
            {invoiceStatusFilter === 'esperando_nf' ? 'Esperando NF'
              : invoiceStatusFilter === 'nf_enviada' ? 'NF Enviada'
              : invoiceStatusFilter === 'sem_nf' ? 'Sem NF'
              : invoiceStatusFilter === 'nao_necessario' ? 'Não Necessário'
              : 'Status NF'}
          </button>

          {hasActiveFilters && (
            <button
              onClick={() => { setSortAlpha(''); setPaymentStatusFilter(''); setInvoiceStatusFilter(''); }}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-slate-500 hover:text-white transition-colors"
              data-testid="clear-filters-btn"
            >
              <X className="h-3.5 w-3.5" /> Limpar filtros
            </button>
          )}
        </div>

        {/* Info Banner */}
        <div className="space-y-2">
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl px-5 py-3 text-blue-300 text-sm flex items-center justify-between" data-testid="payment-info-banner">
            <span>O pagamento será enviado em até 5 dias úteis após o envio da nota fiscal.</span>
            {isAdmin && (
              <button onClick={() => { setBannerDraft(bannerText); setShowBannerSettings(true); }}
                className="p-1.5 text-blue-400/60 hover:text-blue-300 rounded transition-colors" title="Configurar texto adicional"
                data-testid="banner-settings-btn">
                <Settings className="h-4 w-4" />
              </button>
            )}
          </div>
          {bannerText && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-5 py-3 text-amber-300 text-sm whitespace-pre-wrap" data-testid="custom-banner-text">
              {bannerText}
            </div>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-r from-blue-500/10 to-teal-500/10 border border-blue-500/30 rounded-xl p-4">
            <p className="text-slate-400 text-sm">Total do Período</p>
            <p className="text-2xl font-bold text-white">R$ {totalAmount.toFixed(2)}</p>
          </div>
          <div className="bg-gradient-to-r from-green-500/10 to-teal-500/10 border border-green-500/30 rounded-xl p-4">
            <p className="text-slate-400 text-sm">Total Pago</p>
            <p className="text-2xl font-bold text-green-400">R$ {totalPaid.toFixed(2)}</p>
          </div>
          <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-xl p-4">
            <p className="text-slate-400 text-sm">Pendente</p>
            <p className="text-2xl font-bold text-red-400">R$ {totalPending.toFixed(2)}</p>
          </div>
        </div>

        {/* Bulk Action Bar */}
        {isAdmin && selected.size > 0 && (
          <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl px-5 py-3 flex items-center justify-between" data-testid="bulk-action-bar-payments">
            <span className="text-teal-400 font-medium text-sm">{selected.size} selecionado(s)</span>
            <div className="flex gap-2">
              <button onClick={() => { setBulkEditForm({ video_name: '', year: '', month: '' }); setShowBulkEditModal(true); }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg text-xs font-medium hover:bg-blue-500/30 transition-colors" data-testid="bulk-edit-btn">
                <Edit2 className="h-3.5 w-3.5" /> Editar
              </button>
              <button onClick={handleBulkPay} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-xs font-medium hover:bg-green-500/30 transition-colors" data-testid="bulk-pay-btn">
                <Check className="h-3.5 w-3.5" /> Marcar Pago
              </button>
              <button onClick={handleBulkDelete} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500/30 transition-colors" data-testid="bulk-delete-payments-btn">
                <Trash2 className="h-3.5 w-3.5" /> Excluir
              </button>
              <button onClick={() => setSelected(new Set())} className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Records Table */}
        <PaymentsTable
          records={filteredRecords}
          userMap={userMap}
          isAdmin={isAdmin}
          user={user}
          selected={selected}
          toggleSelect={toggleSelect}
          toggleSelectAll={toggleSelectAll}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={() => { setSortAlpha(''); setPaymentStatusFilter(''); setInvoiceStatusFilter(''); }}
          uploadingPdf={uploadingPdf}
          onPaymentStatusToggle={handlePaymentStatusToggle}
          onToggleNoInvoice={handleToggleNoInvoice}
          onSetNotRequired={handleSetNotRequired}
          onEditClick={handleEditClick}
          onPdfUpload={handlePdfUpload}
          onPdfDownload={handlePdfDownload}
          onDelete={handleDelete}
        />

        {/* New Payment Modal */}
        <NewPaymentModal
          open={showModal}
          onClose={() => setShowModal(false)}
          newRecord={newRecord}
          setNewRecord={setNewRecord}
          employeeSearch={employeeSearch}
          setEmployeeSearch={setEmployeeSearch}
          filteredEmployees={filteredEmployees}
          employeeProjects={employeeProjects}
          setEmployeeProjects={setEmployeeProjects}
          onSubmit={handleCreate}
          getAuthHeaders={getAuthHeaders}
        />

        {/* Bulk Payment Modal */}
        <BulkPaymentModal
          open={showBulkModal}
          onClose={() => setShowBulkModal(false)}
          onCreated={fetchRecords}
          getAuthHeaders={getAuthHeaders}
        />

        {/* Edit Individual Modal */}
        <EditPaymentModal
          open={showEditModal}
          record={editingRecord}
          onClose={() => { setShowEditModal(false); setEditingRecord(null); }}
          editForm={editForm}
          setEditForm={setEditForm}
          editEmployeeSearch={editEmployeeSearch}
          setEditEmployeeSearch={setEditEmployeeSearch}
          editFilteredEmployees={editFilteredEmployees}
          onSubmit={handleEditSubmit}
        />

        {/* Bulk Edit Modal */}
        <BulkEditModal
          open={showBulkEditModal}
          onClose={() => setShowBulkEditModal(false)}
          selectedCount={selected.size}
          form={bulkEditForm}
          setForm={setBulkEditForm}
          onSubmit={handleBulkEditSubmit}
        />

        {/* Banner Settings Modal */}
        <BannerSettingsModal
          open={showBannerSettings}
          onClose={() => setShowBannerSettings(false)}
          draft={bannerDraft}
          setDraft={setBannerDraft}
          onSave={saveBannerText}
        />
      </div>
    </Layout>
  );
};

export default Budgets;
